import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Save, 
  RefreshCw, 
  Bell, 
  Shield, 
  Database, 
  Mail, 
  Globe, 
  Lock, 
  Users, 
  FileText, 
  Clock, 
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Info,
  Zap,
  HardDrive,
  Wrench,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

// Import existing page components to use as tab content
import GeneralSettings from './GeneralSettings';
import EmailConfiguration from './EmailConfiguration';
import SecuritySettings from './SecuritySettings';
import IntegrationManagement from './IntegrationManagement';
import CacheManagement from './CacheManagement';
import SystemMaintenance from './SystemMaintenance';
import BusinessHoursSetup from './BusinessHoursSetup';

const UnifiedSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync activeTab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'general';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const tabs = [
    { 
      id: 'general', 
      label: 'General', 
      icon: SettingsIcon,
      description: 'System information and basic settings'
    },
    { 
      id: 'email', 
      label: 'Email', 
      icon: Mail,
      description: 'Email configuration and SMTP settings'
    },
    { 
      id: 'security', 
      label: 'Security', 
      icon: Shield,
      description: 'Security policies and authentication'
    },
    { 
      id: 'integrations', 
      label: 'Integrations', 
      icon: Zap,
      description: 'API keys, webhooks, and third-party integrations'
    },
    { 
      id: 'cache', 
      label: 'Cache', 
      icon: Database,
      description: 'Cache configuration and management'
    },
    { 
      id: 'system', 
      label: 'System Maintenance', 
      icon: Wrench,
      description: 'System maintenance and monitoring'
    },
    { 
      id: 'business-hours', 
      label: 'Business Hours', 
      icon: Clock,
      description: 'Business hours and timezone settings'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <SettingsIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">System Settings</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Configure all system settings from a single unified interface
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard')}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50 dark:bg-red-900/20' : 'border-green-200 bg-green-50 dark:bg-green-900/20'}>
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          )}
          <AlertDescription className={message.type === 'error' ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-1 overflow-x-auto px-4" aria-label="Settings Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-[#006600] text-[#006600]'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
                    }`}
                    title={tab.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'general' && (
              <div>
                <GeneralSettings />
              </div>
            )}

            {activeTab === 'email' && (
              <div>
                <EmailConfiguration />
              </div>
            )}

            {activeTab === 'security' && (
              <div>
                <SecuritySettings />
              </div>
            )}

            {activeTab === 'integrations' && (
              <div>
                <IntegrationManagement />
              </div>
            )}

            {activeTab === 'cache' && (
              <div>
                <CacheManagement />
              </div>
            )}

            {activeTab === 'system' && (
              <div>
                <SystemMaintenance />
              </div>
            )}

            {activeTab === 'business-hours' && (
              <div>
                <BusinessHoursSetup />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedSettings;

